import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, AlertTriangle } from "lucide-react";
import PlayfulNavbar from "../components/playful/PlayfulNavbar";
import PlayfulFooter from "../components/playful/PlayfulFooter";
import { CandyButton } from "../components/playful/CandyButton";
import { PasswordInput } from "../components/playful/PasswordInput";
import { PasswordStrength } from "../components/playful/PasswordStrength";
import { StickerCard } from "../components/playful/StickerCard";
import { GeometricBadge } from "../components/playful/GeometricBadge";
import { FloatingIcon } from "../components/playful/FloatingIcon";
import { Blob } from "../components/playful/Decor";
import { api, formatApiError } from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [state, setState] = useState({ loading: true, valid: false, reason: "" });
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [confirmError, setConfirmError] = useState("");

  useEffect(() => {
    if (!token) {
      setState({ loading: false, valid: false, reason: "missing_token" });
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/auth/validate-reset-token`, { params: { token } });
        setState({ loading: false, valid: !!data.valid, reason: data.reason || "" });
      } catch (err) {
        setState({ loading: false, valid: false, reason: formatApiError(err) });
      }
    })();
  }, [token]);

  const onConfirmChange = (e) => {
    setConfirm(e.target.value);
    if (e.target.value && e.target.value !== pwd) {
      setConfirmError("Passwords do not match");
    } else {
      setConfirmError("");
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    if (pwd !== confirm) {
      setConfirmError("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pwd });
      setDone(true);
      toast.success("Password updated. Please sign in.");
      setTimeout(() => navigate("/login"), 1200);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen overflow-x-hidden bg-paper text-ink"
      data-testid="reset-password-page"
    >
      <PlayfulNavbar />
      <section className="relative mx-auto max-w-[1400px] px-5 pb-32 pt-16 md:px-10">
        <Blob
          tone="candy"
          size={340}
          className="absolute -left-24 -top-24 animate-float opacity-70"
        />
        <Blob tone="mint" size={220} className="absolute right-6 top-20 animate-drift opacity-70" />

        <div className="relative grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-6 md:pr-10">
            <GeometricBadge tone="mint">RESET — STEP 2</GeometricBadge>
            <h1 className="mt-4 font-display text-5xl font-extrabold leading-[0.95] tracking-tight md:text-7xl">
              Set a new password.
              <span className="block text-candy">Quick and painless.</span>
            </h1>
            <p className="mt-6 max-w-md font-sans text-lg">
              Choose a new password. Your LinkLane reset link expires one hour after issue and may
              only be used once.
            </p>
            <div className="mt-8">
              <FloatingIcon icon={KeyRound} tone="candy" size={60} rotate={-8} />
            </div>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8">
            {state.loading ? (
              <StickerCard tone="candy" bg="paper" hover={false} className="p-8">
                <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-muted-foreground">
                  VERIFYING TOKEN…
                </span>
              </StickerCard>
            ) : !state.valid ? (
              <StickerCard
                tone="pink"
                bg="paper"
                hover={false}
                className="flex flex-col gap-5 p-8"
                data-testid="reset-invalid"
              >
                <div className="flex items-center gap-3">
                  <FloatingIcon icon={AlertTriangle} tone="pink" size={44} />
                  <GeometricBadge tone="pink">
                    TOKEN {state.reason === "expired" ? "EXPIRED" : "INVALID"}
                  </GeometricBadge>
                </div>
                <p className="font-display text-2xl font-bold">
                  This reset link is no longer usable. Request a new one.
                </p>
                <CandyButton to="/forgot" variant="candy" withArrow>
                  Request a new link
                </CandyButton>
                <div className="text-center font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                  <Link to="/login" className="text-candy underline-offset-4 hover:underline">
                    BACK TO LOGIN
                  </Link>
                </div>
              </StickerCard>
            ) : (
              <StickerCard tone="candy" bg="paper" hover={false} className="p-8 md:p-10">
                <form onSubmit={onSubmit} className="flex flex-col gap-5" data-testid="reset-form">
                  <div>
                    <PasswordInput
                      required
                      label="New password"
                      value={pwd}
                      onChange={(e) => setPwd(e.target.value)}
                      placeholder="At least 6 characters"
                      autoFocus
                      data-testid="reset-password-input"
                    />
                    <PasswordStrength password={pwd} />
                  </div>
                  <PasswordInput
                    required
                    label="Confirm"
                    value={confirm}
                    onChange={onConfirmChange}
                    placeholder="Repeat your new password"
                    error={confirmError}
                    data-testid="reset-confirm-input"
                  />
                  <CandyButton
                    type="submit"
                    variant="candy"
                    size="lg"
                    block
                    disabled={busy || done}
                    withArrow
                    data-testid="reset-submit-btn"
                  >
                    {done ? "Updated" : busy ? "Updating…" : "Update password"}
                  </CandyButton>
                </form>
              </StickerCard>
            )}
          </div>
        </div>
      </section>
      <PlayfulFooter />
    </div>
  );
}
